/**
 * Query keys de fiscal — fiscal: VeriFACTU/AEAT/TicketBAI, SILTRA y normativa adoptada.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const fiscalKeys = {
  fiscal: {
    census: (companyId?: string, userId?: string, accessScope?: string) => ['fiscal-census', companyId, userId, accessScope] as const,
    verifactu: (companyId?: string) => ['verifactu-records', companyId] as const,
    aeatSubmissions: (companyId?: string) => ['aeat-submissions', companyId] as const,
    ticketBaiConfig: (companyId?: string) => ['ticketbai-config', companyId] as const,
    // L-02b §3.2 — pantalla de TicketBAI: pendientes de enviar (calculados en el
    // servidor), contadores por estado y envíos del rango. Sustituyen a claves
    // literales y a la de facturas sin filtrar que la página compartía.
    ticketBaiPendientes: (companyId?: string) => ['ticketbai-pending', companyId] as const,
    ticketBaiResumen: (companyId?: string) => ['ticketbai-summary', companyId] as const,
    ticketBaiLogs: (companyId?: string, filters?: unknown) => ['ticketbai-logs', companyId, filters] as const,
    taxAdjustments: (taxYearId?: string) => ['tax-adjustments', taxYearId] as const,
    complianceStatus: (companyId?: string) => ['fiscal-compliance-status', companyId] as const,
    certificate: (companyId?: string) => ['fiscal-certificate', companyId] as const,
    // Pieza (k) de la 1.0.1. Dos claves y no una porque son dos lecturas con vidas
    // distintas: las comprobaciones cambian cuando la empresa cambia (sube un
    // certificado, rellena el NIF), y los literales cambian cuando los cambia el
    // asesor. Invalidar una no tiene por qué invalidar la otra.
    // F5: la modalidad entra en la clave solo cuando se pide (No VERI*FACTU). Sin ella la
    // clave es la de siempre, y invalidar `(companyId)` alcanza a las dos por prefijo.
    verifactuActivacion: (companyId?: string, modalidad?: 'verifactu' | 'no_verifactu') =>
      (modalidad ? ['verifactu-activacion', companyId, modalidad] : ['verifactu-activacion', companyId]) as readonly unknown[],
    verifactuTextos: (companyId?: string) => ['verifactu-textos', companyId] as const,
    // F5 · el aviso de alarma (F2, `GET /verifactu/alarmas`). Lo leen el banner del layout y
    // la tarjeta de la pantalla de VERI*FACTU con la MISMA clave: es el mismo dato.
    verifactuAlarmas: (companyId?: string) => ['verifactu-alarmas', companyId] as const,
    // F5b · la custodia No VERI*FACTU (F3). Los jobs de comprobación por ámbito (las dos cadenas
    // son dos listas), uno con su progreso, y la comprobación de UN registro o evento: es la que
    // navega, así que el `id` entra en la clave y «Siguiente» es otra entrada de caché.
    verifactuComprobaciones: (companyId?: string, ambito?: 'registros_facturacion' | 'registros_evento') =>
      ['verifactu-comprobaciones', companyId, ambito] as const,
    verifactuComprobacion: (companyId?: string, id?: string) => ['verifactu-comprobacion', companyId, id] as const,
    verifactuComprobacionRegistro: (companyId?: string, id?: string) =>
      ['verifactu-comprobacion-registro', companyId, id] as const,
    // El libro de eventos: los filtros (tipos, periodo, página) entran en la clave. Su PREFIJO va
    // aparte, como `auto130Todos`: `verifactuEventos(cid)` acaba en `undefined` y no casaría con
    // ninguna entrada filtrada. Lo invalida el fin de una comprobación, que registra sus 03-06.
    verifactuEventos: (companyId?: string, filtros?: unknown) => ['verifactu-eventos', companyId, filtros] as const,
    verifactuEventosTodos: (companyId?: string) => ['verifactu-eventos', companyId] as const,
    verifactuComprobacionEvento: (companyId?: string, id?: string) =>
      ['verifactu-comprobacion-evento', companyId, id] as const,
    // F5c · consulta de los registros firmados (F4a), con sus filtros y página en la clave.
    verifactuRegistrosFirmados: (companyId?: string, filtros?: unknown) =>
      ['verifactu-registros-firmados', companyId, filtros] as const,
    // Las exportaciones por ámbito (F4b) y una con su descarga. La de una NO se cachea para descargar:
    // su URL vive 300 s, así que «Descargar» la pide en el momento.
    verifactuExportaciones: (companyId?: string, ambito?: 'registros_facturacion' | 'registros_evento') =>
      ['verifactu-exportaciones', companyId, ambito] as const,
    verifactuExportacion: (companyId?: string, id?: string) => ['verifactu-exportacion', companyId, id] as const,
    // Los requerimientos (F4c): uno solo en curso por empresa, sin ámbito.
    verifactuRequerimientos: (companyId?: string) => ['verifactu-requerimientos', companyId] as const,
    verifactuRequerimiento: (companyId?: string, id?: string) => ['verifactu-requerimiento', companyId, id] as const,

    // (b3) · el aviso de pendientes, que se lee desde el LAYOUT y por tanto desde
    // cualquier pantalla. Clave propia y no la de la pantalla de VeriFACTU: ésta se
    // refresca sola cada minuto, y compartir caché con la pantalla haría que el aviso
    // global marcara el ritmo de un listado que el usuario quizá no está mirando.
    verifactuAviso: (companyId?: string) => ['verifactu-aviso-pendientes', companyId] as const,
    companyFiscalData: (companyId?: string) => ['company-fiscal-data', companyId] as const,
    // El registro de socios. Es fiscal y no contable aunque se pinte en la lente
    // de Patrimonio: quien lo consume es el Modelo 123 —su casilla [01] cuenta
    // perceptores— y, cuando exista, el 193. La ruta del API está en `/fiscal/`
    // por la misma razón.
    shareholders: (companyId?: string) => ['company-shareholders', companyId] as const,
    // Las rentas del capital mobiliario que NO son dividendos: intereses de
    // préstamos de socios, cuentas en participación, cánones. Son las casillas
    // [02], [05] y [08] del Modelo 123, que hasta ahora se tecleaban en el propio
    // formulario del modelo sin que nada las comprobara.
    capitalIncomeEvents: (companyId?: string) => ['capital-income-events', companyId] as const,
    // El tipo de retención que la norma fija para cada clase de renta. Lo sirve el
    // backend a propósito: reimplementar esa tabla aquí sería la cuarta vez que
    // una lista fiscal del cliente se separa de la del servidor en este repo.
    capitalIncomeTipos: (companyId?: string) => ['capital-income-tipos', companyId] as const,
    // Gate S0 — la afirmación de que los libros están completos (#2137). El
    // EJERCICIO entra en la clave: la afirmación es una sola fecha por empresa,
    // pero lo que devuelve el endpoint es su EVALUACIÓN contra un año concreto, y
    // la misma fecha da estados distintos según el ejercicio. Sin el año en la
    // clave, mirar 2025 después de 2026 enseñaría el veredicto de 2026.
    librosCompletos: (companyId?: string, year?: number) =>
      ['libros-completos', companyId, year] as const,
    // Cálculo de modelos en el backend (`/api/v1/aeat/modelos/*`). El 190 es
    // anual y el 111 trimestral, y los dos salen del MISMO motor a propósito: el
    // 190 tiene que cuadrar con la suma de los cuatro 111, y ese cuadre no
    // significa nada si cada lado se calcula en un sitio distinto.
    modelo190: (companyId?: string, year?: number) => ['modelo-190', companyId, year] as const,
    modelo111: (companyId?: string, year?: number, trimestre?: string) =>
      ['modelo-111', companyId, year, trimestre] as const,
    // Impuesto sobre Sociedades. `qs` son los overrides que el usuario teclea, ya
    // serializados: entran en la clave porque el motor devuelve otro importe con cada
    // juego, igual que `modalidad` en el 202.
    modelo200: (companyId?: string, year?: number, qs?: string) =>
      ['modelo-200', companyId, year, qs] as const,
    // Elegibilidad REAV (art. 48-49 LIS). La consultan DOS pantallas —`Modelo200Form` y
    // `ReavPanel`— contra el mismo endpoint, y hasta #2404 lo hacían con claves distintas:
    // una llevaba `companyId` y la otra no, así que el mismo dato vivía dos veces en la
    // caché y solo una de las dos entradas se aislaba por empresa. De ahí que la clave
    // viva aquí y no en cada componente.
    reavEligibility: (companyId?: string, year?: number) =>
      ['reav-eligibility', companyId, year] as const,
    // Pago fraccionado del IS. `modalidad` entra en la clave porque las dos del
    // art. 40 dan importes distintos sobre el mismo periodo, y `retenciones`
    // porque minoran la base: sin ellas dos consultas diferentes compartirían
    // caché y la segunda mostraría el importe de la primera.
    modelo202: (
      companyId?: string,
      year?: number,
      periodo?: string,
      modalidad?: string | null,
      retenciones?: number,
    ) => ['modelo-202', companyId, year, periodo, modalidad ?? null, retenciones ?? 0] as const,
    // Prefijo de TODOS los cálculos del 202 de una empresa, para invalidarlos de golpe
    // cuando cambia un dato del que dependen (una entrada manual, por ejemplo). Lleva
    // `companyId` a propósito: sin él se invalidaría también la caché de otras empresas.
    modelo202Todos: (companyId?: string) => ['modelo-202', companyId] as const,
    // «Las opciones del modelo 202» — configuración por ejercicio del pago
    // fraccionado. `añoVentanas` entra en la clave y NO es el ejercicio: en
    // ejercicio partido un año natural de pagos cruza DOS ejercicios, y es el
    // servidor quien los deriva. Compartir caché entre años enseñaría los
    // ejercicios del año anterior como si fueran los que hay que configurar.
    opciones202: (companyId?: string, añoVentanas?: number) =>
      ['opciones-modelo-202', companyId, añoVentanas] as const,
    // La FILA de un ejercicio concreto de «Las opciones del modelo 202».
    //
    // Va aparte de `opciones202` y no es duplicarla: aquélla es la DERIVACIÓN del
    // servidor —qué ejercicios hay que configurar para las ventanas de un año, y qué
    // falta en cada uno— y no incluye el régimen cooperativo. Ésta es la fila cruda,
    // que es donde viven `entity_type`, `cooperative_tax_status` y `has_credit_section`.
    //
    // El EJERCICIO en la clave, no el año de ventanas: el régimen se declara por
    // ejercicio, y en ejercicio partido dos fichas de la misma pantalla son dos
    // ejercicios distintos. Compartir caché enseñaría el régimen de uno en el otro.
    opciones202Ejercicio: (companyId?: string, ejercicio?: number) =>
      ['opciones-modelo-202-ejercicio', companyId, ejercicio] as const,
    // Entradas manuales del 202 (Asistente de Acumulados). La clave lleva el EJERCICIO,
    // no el año de las ventanas: la apertura y la cuota del 200 pertenecen a ejercicios
    // distintos, y compartir caché entre ellos enseñaría la entrada de un ejercicio como
    // si fuera la de otro.
    entradasManuales202: (companyId?: string, ejercicio?: number) =>
      ['entradas-manuales-202', companyId, ejercicio] as const,
    // Los pagos fraccionados del 130 presentados FUERA de VERIX — el contribuyente
    // migrado a mitad de año. Va por EJERCICIO y no por trimestre aunque cada entrada
    // sea trimestral: la pantalla las pide todas juntas (son las cuatro fichas de un
    // año) y el motor las lee todas para cualquier trimestre. Partirla por trimestre
    // multiplicaría por cuatro las peticiones para servir siempre lo mismo.
    pagosPrevios130: (companyId?: string, ejercicio?: number) =>
      ['entradas-manuales-130', companyId, ejercicio] as const,
    // Entradas manuales del 303 — hoy, la respuesta «¿concurso en este trimestre?»
    // (indicador 19). Por EJERCICIO, como la del 130: una sola petición sirve los cuatro
    // trimestres, y la pantalla elige el suyo por `tax_period`.
    entradasManuales303: (companyId?: string, ejercicio?: number) =>
      ['entradas-manuales-303', companyId, ejercicio] as const,
    // El pre-relleno del 130. Estaba como array literal dentro de `Modelo130Form`, así
    // que nadie de fuera podía invalidarlo: aportar un trimestre externo dejaba el
    // formulario enseñando la [05] vieja hasta recargar a mano.
    auto130: (companyId?: string, year?: number, period?: string) =>
      ['tax-filings', companyId, 'auto-130', year, period] as const,
    // 🔴 El PREFIJO, para invalidar los cuatro trimestres de golpe. No vale llamar a
    // `auto130(companyId)` sin año ni período: eso produce `[..., undefined, undefined]`,
    // y React Query compara índice a índice — `undefined` no casa con `2026`, así que no
    // invalidaría NADA. Falla en silencio: el dato se guarda bien y la pantalla sigue
    // enseñando la [05] vieja.
    auto130Todos: (companyId?: string) => ['tax-filings', companyId, 'auto-130'] as const,
    // TODAS las entradas manuales del 130 de la empresa, de cualquier ejercicio. Hace
    // falta porque el rendimiento del ejercicio anterior se guarda con `tax_year` =
    // ejercicio − 1: invalidar solo `pagosPrevios130(companyId, ejercicio)` dejaría esa
    // otra consulta enseñando el dato viejo. Prefijo de `pagosPrevios130`.
    entradasManuales130Todas: (companyId?: string) => ['entradas-manuales-130', companyId] as const,
    // El resultado COMPLETO del motor del 130 (`GET /aeat/modelos/130`): la sección III y
    // las marcas de indeterminado, que `/auto-130` no trae. Es lo que va al fichero.
    modelo130Motor: (companyId?: string, year?: number, trimestre?: string) =>
      ['aeat', companyId, 'modelo-130', year, trimestre] as const,
    // El PREFIJO, por lo mismo que `auto130Todos`: sin él, un `undefined` al final no casa.
    modelo130MotorTodos: (companyId?: string) => ['aeat', companyId, 'modelo-130'] as const,
    // Retenciones del capital mobiliario. Trimestral y sin overrides: el motor
    // agrega, y la única cifra que aporta el usuario (la casilla [01]) no entra
    // en el cálculo, así que no forma parte de la clave.
    modelo123: (companyId?: string, year?: number, trimestre?: string) =>
      ['modelo-123', companyId, year, trimestre] as const,
    // La otra mitad del 123: el IRNR de los perceptores NO residentes. Misma forma
    // de clave y por lo mismo — se deriva entero del reparto, sin overrides.
    modelo216: (companyId?: string, year?: number, trimestre?: string) =>
      ['modelo-216', companyId, year, trimestre] as const,
    // El resumen ANUAL del 123. Sin trimestre en la clave porque no lo tiene:
    // meter uno a `undefined` haría que dos vistas distintas compartieran caché
    // sin que se notara.
    modelo193: (companyId?: string, year?: number) =>
      ['modelo-193', companyId, year] as const,
    // El resumen ANUAL del 216. Misma forma que la del 193 y por lo mismo: sin
    // trimestre, porque no lo tiene.
    modelo296: (companyId?: string, year?: number) =>
      ['modelo-296', companyId, year] as const,
    // Plazo de remisión al SII de las facturas expedidas del mes (art. 69 bis.1.a
    // RIVA). `mes` y `ejercicio` en la clave porque el panel los cambia con dos
    // selects: compartir caché entre meses enseñaría el riesgo del mes anterior.
    siiPlazosEmitidas: (companyId?: string, ejercicio?: string, mes?: string) =>
      ['sii-plazos-emitidas', companyId, ejercicio, mes] as const,
  },

  // ── SILTRA INBOUND — ficheros de respuesta TGSS subidos por la empresa ─────
  // Per-company (companyId). `type` (tt/calculos/respuestas/rnt/rlc/dcl) primero
  // para invalidar un tipo concreto; `period`/`ccc` son los filtros del listado.
  // Snapshot inmutable → solo upload (invalida la list del tipo+empresa), sin edición.
  siltraInbound: {
    all: () => ['siltra-inbound'] as const,
    list: (type?: string, companyId?: string, period?: string | null, ccc?: string | null) =>
      ['siltra-inbound', type, companyId, period ?? null, ccc ?? null] as const,
    detail: (type?: string, id?: string) =>
      ['siltra-inbound', type, 'detail', id] as const,
  },

  // ── SILTRA reconciliation — cuadre RNT (bases TGSS) ↔ nóminas calculadas ────
  // Per-company + período (+ccc opcional). Read-only (consume verix-api#588).
  siltraReconciliation: {
    view: (companyId?: string, period?: string | null, ccc?: string | null) =>
      ['siltra-reconciliation', companyId, period ?? null, ccc ?? null] as const,
  },

  // ── #2115 — inmuebles ARRENDADOS por la empresa (lado GASTO) ───────────────
  // Maestro del registro tipo 2 del Modelo 180 (perceptor × inmueble). NO es
  // `revenueUnit`: aquélla es la cartera propia, donde la empresa COBRA.
  // GET/POST /api/rented-properties.
  rentedProperty: {
    all: () => ['rented-property'] as const,
    list: (companyId?: string) => ['rented-property', companyId] as const,
  },

  // ── F2.d — adopción de normas por empresa (Configuración → Normativa) ─────
  // GET/POST /api/settings/regulatory-parameters(/adopt).
  regulatoryParameters: {
    all: () => ['regulatory-parameters-settings'] as const,
    settings: (companyId?: string) => ['regulatory-parameters-settings', companyId] as const,
    detail: (sourceFuente?: string, scope?: string, validFrom?: string) =>
      ['regulatory-parameters-detail', sourceFuente, scope, validFrom] as const,
  },
} as const;
